import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, CirclePlay, GraduationCap, ListChecks } from 'lucide-react';
import { Icone } from '../components/Icone';
import { Chargement, EcranEnErreur, Pastille } from '../components/ui/Primitives';
import {
  pourcentagePresence,
  useMaFormationApprenant,
  type BlocEnProgression,
  type CoursEnProgression,
  type MaFormationApprenant,
  type ModuleApprenant,
  type QuizEnProgression,
  type SeanceApprenant,
} from '../lib/useEspaceApprenant';
import { adresseLecon } from '../lib/useLecteur';
import { ErreurApi } from '../lib/api';
import { momentRelatif } from '../lib/moment-relatif';
import { adresseDevoir, echeanceLongue } from '../lib/affichage-devoirs';
import { BlocsEcf, EvaluationsBloc } from '../components/ecf/BlocsEcf';
import { BarreAvancement, tauxLisible } from '../components/lecteur/Progression';

/**
 * « Mes formations » (écran `mycourses`, maquette
 * `docs/maquette/modules/03-pages-dashboard-catalogue.js`).
 *
 * L'apprenant suit une formation, celle de son dossier — seule visible ici —
 * et, en mode OF, une session. Deux avancements sont dits, jamais confondus :
 * les heures réellement **émargées** face au volume du programme, et la
 * progression dans les cours **en ligne** (TF-451) — leçons terminées et quiz
 * faits —, bloc par bloc, cours par cours, module par module. Chaque bloc
 * porte aussi son ECF, telle que l'évaluateur l'a saisie (CDC §7.5).
 *
 * Tous les taux viennent de l'API (`domain/progression-formation.ts`) : une
 * leçon est terminée quand sa vidéo est vue jusqu'au bout, ou, sans vidéo,
 * dès que l'apprenant ouvre une leçon qui a des ressources. Les onglets « En
 * cours / Terminées / À venir » de la maquette n'ont pas d'objet : un compte
 * apprenant porte un seul dossier, donc une seule formation.
 */

function dateCourte(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return `${jour}/${mois}/${annee}`;
}

function initiales(nom: string): string {
  return nom
    .split(' ')
    .map((partie) => partie[0] ?? '')
    .join('')
    .toUpperCase();
}

function note(valeur: number): string {
  return String(valeur).replace('.', ',');
}

function LigneSeance({ seance }: { seance: SeanceApprenant }): JSX.Element {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-codialis-navy">
          {dateCourte(seance.date)} · {seance.libelle}
        </div>
        <div className="text-[11px] text-codialis-gray">
          {seance.demiJournee} · {seance.heureDebut}–{seance.heureFin}
          {seance.lieu ? ` · ${seance.lieu}` : ''}
          {seance.formateur ? ` · ${seance.formateur}` : ''}
        </div>
      </div>
      <Pastille texte={seance.statutTexte} ton={seance.statutTon} />
    </div>
  );
}

function Tuile({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card sm:p-5">
      <div className="text-xs uppercase tracking-wide text-codialis-gray">{libelle}</div>
      {children}
    </div>
  );
}

/** Les quatre chiffres de la maquette. */
function Chiffres({ data }: { data: MaFormationApprenant }): JSX.Element {
  const { formation, emargement, progression } = data;
  const prevues = formation?.heuresPrevues ?? 0;
  const parHeures = prevues > 0 ? Math.min(100, Math.floor((emargement.heuresSuivies / prevues) * 100)) : null;
  const ouverts = progression.refus === null;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Tuile libelle="Heures suivies">
        <div className="font-display mt-1 text-3xl font-extrabold text-codialis-navy">
          {note(emargement.heuresSuivies)}
          <span className="text-base text-codialis-gray">h</span>
        </div>
        <div className="mt-3">
          <BarreAvancement
            avancement={{ faits: emargement.heuresSuivies, total: prevues, taux: parHeures }}
            libelle="Heures émargées sur le volume du programme"
            hauteur="h-1"
          />
        </div>
        <div className="mt-1 text-xs text-codialis-gray">
          sur {prevues} h prévues · présence {pourcentagePresence(emargement.tauxPresence)}
        </div>
      </Tuile>

      <Tuile libelle="Modules complétés">
        <div className="font-display mt-1 text-3xl font-extrabold text-codialis-navy">
          {ouverts ? progression.modules.completes : '—'}
          {ouverts && <span className="text-base text-codialis-gray">/{progression.modules.total}</span>}
        </div>
        <div className="mt-3">
          <BarreAvancement
            avancement={progression.formation}
            libelle="Progression dans les cours en ligne"
            hauteur="h-1"
          />
        </div>
        <div className="mt-1 text-xs text-codialis-gray">
          {!ouverts
            ? 'cours en ligne pas encore ouverts'
            : progression.formation.taux === null
              ? 'aucun cours en ligne à suivre'
              : `${tauxLisible(progression.formation)} du parcours en ligne`}
        </div>
      </Tuile>

      <Tuile libelle="Score moyen">
        <div className="font-display mt-1 text-3xl font-extrabold text-codialis-mint">
          {progression.quiz.scoreMoyen === null ? '—' : note(progression.quiz.scoreMoyen)}
          {progression.quiz.scoreMoyen !== null && <span className="text-base text-codialis-gray">/20</span>}
        </div>
        <div className="mt-3 text-xs text-codialis-gray">
          {!ouverts
            ? 'cours en ligne pas encore ouverts'
            : progression.quiz.total === 0
              ? 'aucun quiz à votre programme'
              : `${progression.quiz.faits} quiz fait${progression.quiz.faits > 1 ? 's' : ''} sur ${progression.quiz.total}`}
        </div>
      </Tuile>

      <Tuile libelle="Certification visée">
        <div className="font-display mt-1 truncate text-2xl font-extrabold text-codialis-navy">{formation?.rncp}</div>
        <div className="mt-3 text-xs text-codialis-gray">
          {formation?.niveau} · code diplôme {formation?.codeDiplome}
        </div>
      </Tuile>
    </div>
  );
}

/** La carte de la formation : où en est l'apprenant, et de quoi reprendre. */
function CarteFormation({ data }: { data: MaFormationApprenant }): JSX.Element {
  const navigate = useNavigate();
  const { formation, progression, reprise } = data;
  const cours = reprise.cours;
  if (!formation) return <div />;

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="grid grid-cols-1 md:grid-cols-5">
        <div className="flex h-28 items-center justify-center bg-gradient-to-br from-codialis-sky to-codialis-blue md:h-auto">
          <GraduationCap className="h-12 w-12 text-white/90" aria-hidden />
        </div>
        <div className="min-w-0 border-gray-100 p-4 sm:p-5 md:col-span-3 md:border-r">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="badge-info rounded px-2 py-0.5 text-[10px] font-medium">
              {formation.code} · {formation.heuresPrevues} h
            </span>
            <span className="badge-success rounded px-2 py-0.5 text-[10px] font-medium">
              {data.mode === 'CFA' ? 'Apprentissage' : 'Formation continue'}
            </span>
          </div>
          <h2 className="font-display mb-1 text-lg font-bold text-codialis-navy">{formation.titre}</h2>
          <p className="mb-3 text-sm text-codialis-gray">
            {data.session
              ? `Session ${data.session.libelle} · du ${dateCourte(data.session.dateDebut)} au ${dateCourte(data.session.dateFin)}`
              : data.dateEntree
                ? `Entrée en formation le ${dateCourte(data.dateEntree)}${
                    data.dateFinPrevue ? ` · fin prévue le ${dateCourte(data.dateFinPrevue)}` : ''
                  }`
                : 'Dates de parcours non renseignées.'}
          </p>

          {progression.refus ? (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">{progression.refus}</p>
          ) : (
            <div className="mb-3">
              {cours && (
                <p className="mb-1 text-xs text-codialis-gray">
                  {cours.bloc ? `Bloc ${cours.bloc.numero} sur ${cours.bloc.total} · ` : ''}
                  Module {cours.lecon.module.numero}/{cours.nombreModules} · {cours.titre}
                </p>
              )}
              <div className="mb-1 flex flex-wrap justify-between gap-x-2 text-xs">
                <span className="min-w-0 text-codialis-gray">
                  {cours ? (
                    <>
                      Module en cours : <strong className="text-codialis-navy">{cours.lecon.module.titre}</strong>
                    </>
                  ) : (
                    'Aucun cours en ligne publié pour l’instant'
                  )}
                </span>
                <span className="font-semibold text-codialis-mint-clair">{tauxLisible(progression.formation)}</span>
              </div>
              <BarreAvancement avancement={progression.formation} libelle="Progression de la formation" hauteur="h-2" />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-codialis-gray">
            {data.formateurs.map((formateur) => (
              <span key={formateur} className="flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-[9px] font-bold text-white">
                  {initiales(formateur)}
                </span>
                {formateur}
              </span>
            ))}
            {progression.derniereActivite && (
              <span>Dernière activité : {momentRelatif(progression.derniereActivite)}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col justify-center gap-2 bg-gray-50 p-4 sm:p-5">
          {cours && (
            <button
              type="button"
              onClick={() => navigate(adresseLecon(cours.lecon.id))}
              className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
            >
              <CirclePlay className="h-4 w-4" aria-hidden />
              {cours.regardeLe ? 'Reprendre' : 'Commencer'}
            </button>
          )}
          <a
            href="#programme-formation"
            className="inline-flex min-h-[36px] items-center justify-center text-xs font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
          >
            Voir le détail
          </a>
        </div>
      </div>
    </div>
  );
}

function CoursDuBloc({ cours }: { cours: CoursEnProgression }): JSX.Element {
  const navigate = useNavigate();
  const debut = cours.prochaineLecon;
  return (
    <li className="rounded-xl border border-gray-100 p-3 sm:p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1 basis-48">
          <div className="font-semibold text-codialis-navy">{cours.titre}</div>
          <div className="text-xs text-codialis-gray">Par {cours.auteur}</div>
        </div>
        {debut && (
          <button
            type="button"
            onClick={() => navigate(adresseLecon(debut.id))}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
          >
            <CirclePlay className="h-4 w-4 text-codialis-sky" aria-hidden />
            Ouvrir<span className="sr-only"> le cours {cours.titre}</span>
          </button>
        )}
      </div>
      <div className="mt-2 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <BarreAvancement avancement={cours.avancement} libelle={`Progression du cours ${cours.titre}`} />
        </div>
        <span className="w-10 shrink-0 text-right text-xs font-semibold text-codialis-navy">
          {tauxLisible(cours.avancement)}
        </span>
      </div>
      <ol className="mt-2 divide-y divide-gray-50">
        {cours.modules.map((module, rang) => (
          <li key={module.id} className="flex items-center gap-2 py-1.5 text-sm">
            {module.avancement.taux === 100 ? (
              <>
                <Check className="h-4 w-4 shrink-0 text-codialis-mint" aria-hidden />
                <span className="sr-only">Complété :</span>
              </>
            ) : (
              <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300" aria-hidden />
            )}
            <span className="min-w-0 flex-1 truncate text-codialis-navy">
              Module {rang + 1} · {module.titre}
            </span>
            <span className="shrink-0 text-xs text-codialis-gray">
              {module.avancement.taux === null
                ? 'à venir'
                : `${module.avancement.faits}/${module.avancement.total} · ${tauxLisible(module.avancement)}`}
            </span>
          </li>
        ))}
      </ol>
    </li>
  );
}

function QuizDuBloc({ quiz }: { quiz: QuizEnProgression }): JSX.Element {
  return (
    <li>
      <Link
        to={`${adresseDevoir({ id: quiz.id, type: 'QUIZ' })}?retour=${encodeURIComponent('/mycourses')}`}
        className="flex min-h-[44px] flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-2 py-2 transition hover:bg-gray-50"
      >
        <ListChecks
          className={`h-4 w-4 shrink-0 ${quiz.fait ? 'text-codialis-mint' : 'text-codialis-sky'}`}
          aria-hidden
        />
        <span className="min-w-0 flex-1 basis-40 text-sm text-codialis-navy">
          {quiz.titre}
          {quiz.cours && <span className="block text-[11px] text-codialis-gray">{quiz.cours}</span>}
        </span>
        {quiz.fait ? (
          <Pastille texte={quiz.note === null ? 'Fait' : `Fait · ${note(quiz.note)}/20`} ton="success" />
        ) : (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-codialis-gray">avant le {echeanceLongue(quiz.echeance)}</span>
            <Pastille texte="À faire" ton="warning" />
          </span>
        )}
      </Link>
    </li>
  );
}

/** Un bloc du programme : son volume, son ECF, ses cours en ligne et ses quiz. */
function BlocProgramme({
  bloc,
  numero,
  progression,
  ecf,
  ouvert,
  enLigneOuvert,
}: {
  bloc: ModuleApprenant;
  numero: number;
  progression: BlocEnProgression | undefined;
  ecf: MaFormationApprenant['ecf']['blocs'][number] | undefined;
  ouvert: boolean;
  /** Les cours en ligne sont ouverts au dossier ; sinon le bloc n'en dit rien. */
  enLigneOuvert: boolean;
}): JSX.Element {
  const avancement = progression?.avancement ?? null;
  const fini = avancement !== null && avancement.taux === 100;
  const enLigne = (progression?.cours.length ?? 0) + (progression?.quiz.length ?? 0);
  return (
    <details open={ouvert} className="group border-b border-gray-100 last:border-b-0">
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 transition hover:bg-gray-50 sm:px-5 [&::-webkit-details-marker]:hidden">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white ${
            fini ? 'bg-codialis-mint' : avancement && avancement.faits > 0 ? 'bg-codialis-sky' : 'bg-gray-300'
          }`}
        >
          {fini ? (
            <>
              <Check className="h-4 w-4" aria-hidden />
              <span className="sr-only">Complété</span>
            </>
          ) : (
            numero
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-codialis-gray">Bloc {numero}</span>
          <span className="block text-sm font-semibold text-codialis-navy">{bloc.libelle}</span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-codialis-gray">
            <span>
              {bloc.heures} h prévues
              {progression && progression.cours.length > 0 ? ` · ${progression.cours.length} cours en ligne` : ''}
            </span>
            {ecf && <Pastille texte={`ECF · ${ecf.etatLibelle}`} ton={ecf.etatTon} />}
          </span>
        </span>
        {avancement && avancement.taux !== null && (
          <span className={`shrink-0 text-xs font-bold ${fini ? 'text-codialis-mint' : 'text-codialis-sky'}`}>
            {tauxLisible(avancement)}
          </span>
        )}
        <ChevronDown className="h-4 w-4 shrink-0 text-codialis-gray transition group-open:rotate-180" aria-hidden />
      </summary>
      <div className="space-y-3 px-4 pb-4 sm:px-5 md:pl-[4.25rem]">
        {avancement && avancement.taux !== null && (
          <BarreAvancement avancement={avancement} libelle={`Progression du bloc ${numero}`} />
        )}
        {ecf && ecf.evaluations.length > 0 && <EvaluationsBloc evaluations={ecf.evaluations} />}
        {!enLigneOuvert ? null : enLigne === 0 ? (
          <p className="text-xs text-codialis-gray">Aucun cours en ligne ni quiz pour ce bloc.</p>
        ) : (
          <>
            {progression!.cours.length > 0 && (
              <ul className="space-y-2" aria-label={`Cours en ligne du bloc ${numero}`}>
                {progression!.cours.map((cours) => (
                  <CoursDuBloc key={cours.id} cours={cours} />
                ))}
              </ul>
            )}
            {progression!.quiz.length > 0 && (
              <ul className="divide-y divide-gray-50" aria-label={`Quiz du bloc ${numero}`}>
                {progression!.quiz.map((quiz) => (
                  <QuizDuBloc key={quiz.id} quiz={quiz} />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </details>
  );
}

export function MesFormationsPage(): JSX.Element {
  const { data, isLoading, error } = useMaFormationApprenant();

  if (isLoading) return <Chargement libelle="Chargement de votre formation…" />;
  if (error) {
    return (
      <EcranEnErreur
        titre="Mes formations"
        message={error instanceof ErreurApi ? error.message : 'Votre formation n’a pas pu être chargée.'}
      />
    );
  }
  if (!data) return <EcranEnErreur titre="Mes formations" message="Votre formation n’a pas pu être chargée." />;

  const { formation, progression, reprise } = data;
  const blocs = new Map(progression.blocs.map((bloc) => [bloc.id, bloc]));
  // Le bloc du cours à reprendre s'ouvre d'emblée ; à défaut, le premier.
  const blocCourant = reprise.cours?.bloc?.id ?? formation?.modules[0]?.id ?? null;
  const transversal = progression.transversal;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-codialis-navy">Mes formations</h1>
          <p className="mt-1 text-codialis-gray">Suivez votre progression bloc par bloc</p>
        </div>
        {formation && (
          <Link
            to="/player"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
          >
            <Icone nom="lecture" />
            Lecteur de cours
          </Link>
        )}
      </div>

      {!formation ? (
        <div className="rounded-2xl bg-white p-4 text-center text-sm text-codialis-gray shadow-card sm:p-8">
          Aucune formation n’est encore rattachée à votre dossier. L’administration la renseigne à votre inscription.
        </div>
      ) : (
        <>
          <Chiffres data={data} />
          <CarteFormation data={data} />

          <section
            id="programme-formation"
            aria-labelledby="titre-programme"
            className="scroll-mt-4 overflow-hidden rounded-2xl bg-white shadow-card"
          >
            <div className="border-b border-gray-100 p-4 sm:p-5">
              <h2 id="titre-programme" className="font-display text-lg font-bold text-codialis-navy">
                Programme de la formation
              </h2>
              {data.ecf.bilan && (
                <p className="mt-1 text-xs text-codialis-gray">
                  Évaluations en cours de formation (ECF) : {data.ecf.bilan.realisees}/{data.ecf.bilan.attendues}{' '}
                  réalisée
                  {data.ecf.bilan.realisees > 1 ? 's' : ''}, {data.ecf.bilan.acquises} acquise
                  {data.ecf.bilan.acquises > 1 ? 's' : ''}.
                </p>
              )}
            </div>
            {formation.modules.length === 0 ? (
              <p className="p-4 text-sm text-codialis-gray sm:p-5">
                Le découpage en blocs n’est pas encore renseigné pour cette formation.
              </p>
            ) : (
              formation.modules.map((bloc, rang) => (
                <BlocProgramme
                  key={bloc.id}
                  bloc={bloc}
                  numero={rang + 1}
                  progression={blocs.get(bloc.id)}
                  ecf={data.ecf.blocs.find((un) => un.id === bloc.id)}
                  ouvert={bloc.id === blocCourant}
                  enLigneOuvert={progression.refus === null}
                />
              ))
            )}
            {transversal.cours.length > 0 && (
              <div className="border-t border-gray-100 p-4 sm:p-5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-codialis-navy">Cours transversaux</h3>
                  <span className="text-xs font-bold text-codialis-sky">{tauxLisible(transversal.avancement)}</span>
                </div>
                <ul className="space-y-2" aria-label="Cours transversaux">
                  {transversal.cours.map((cours) => (
                    <CoursDuBloc key={cours.id} cours={cours} />
                  ))}
                </ul>
              </div>
            )}
            {data.ecf.blocs.some((bloc) => bloc.horsProgramme) && (
              <div className="border-t border-gray-100 p-4 sm:p-5">
                <BlocsEcf blocs={data.ecf.blocs.filter((bloc) => bloc.horsProgramme)} />
              </div>
            )}
            <p className="flex items-start gap-1.5 border-t border-gray-100 p-4 text-[11px] text-codialis-gray sm:px-5">
              <Icone nom="ampoule" className="mt-0.5 h-3 w-3 shrink-0" />
              Une leçon est terminée quand sa vidéo est vue jusqu’au bout, ou, sans vidéo, dès que vous ouvrez ses
              ressources ; un quiz compte une fois fait. Les séances au centre comptent dans les heures suivies.
            </p>
          </section>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl bg-white p-4 shadow-card sm:p-5">
              <h3 className="mb-3 text-sm font-semibold text-codialis-navy">Mes prochaines séances</h3>
              <div className="space-y-2">
                {data.prochainesSeances.length === 0 ? (
                  <p className="text-sm text-codialis-gray">Aucune séance planifiée à venir.</p>
                ) : (
                  data.prochainesSeances.map((seance) => <LigneSeance key={seance.presenceId} seance={seance} />)
                )}
              </div>
            </div>

            <div className="rounded-2xl bg-white p-4 shadow-card sm:p-5">
              <h3 className="mb-3 text-sm font-semibold text-codialis-navy">Mes dernières séances</h3>
              <div className="space-y-2">
                {data.dernieresSeances.length === 0 ? (
                  <p className="text-sm text-codialis-gray">Aucune séance passée.</p>
                ) : (
                  data.dernieresSeances.map((seance) => <LigneSeance key={seance.presenceId} seance={seance} />)
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
